import { createFileRoute } from '@tanstack/react-router';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

import { ApiError } from '@/api/client';
import { useMe, useMfaConfirm, useMfaDisable, useMfaEnrol } from '@/api/queries';
import type { MfaEnrolResponse } from '@/api/types';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/_authed/security')({
  component: SecurityPage,
});

function SecurityPage() {
  const { t } = useTranslation();
  const me = useMe();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('security.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('security.subtitle')}</p>
      </header>

      {me.isError && <ErrorBox message={t('common.error')} />}
      {me.isLoading && <Empty title={t('common.loading')} />}

      {me.data && <MfaSection mfaEnabled={me.data.mfa_enabled} />}
    </div>
  );
}

function MfaSection({ mfaEnabled }: { mfaEnabled: boolean }) {
  const { t } = useTranslation();
  const enrol = useMfaEnrol();
  const confirm = useMfaConfirm();
  const disable = useMfaDisable();

  const [enrolment, setEnrolment] = useState<MfaEnrolResponse | null>(null);
  const [code, setCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableOpen, setDisableOpen] = useState(false);

  const onEnrol = () =>
    enrol.mutate(undefined, {
      onSuccess: (resp) => setEnrolment(resp),
      onError: (e) =>
        toast.error(
          e instanceof ApiError ? e.message : (t('security.enrol_failed') as string),
        ),
    });

  const onConfirm = () =>
    confirm.mutate(code, {
      onSuccess: () => {
        toast.success(t('security.enabled_toast') as string);
        setEnrolment(null);
        setCode('');
      },
      onError: (e) =>
        toast.error(
          e instanceof ApiError ? e.message : (t('security.confirm_failed') as string),
        ),
    });

  const onDisable = () =>
    disable.mutate(disablePassword, {
      onSuccess: () => {
        toast.success(t('security.disabled_toast') as string);
        setDisablePassword('');
        setDisableOpen(false);
      },
      onError: (e) =>
        toast.error(
          e instanceof ApiError ? e.message : (t('security.disable_failed') as string),
        ),
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {mfaEnabled ? (
            <>
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> {t('security.mfa_title')}
              <Badge variant="success">{t('common.active')}</Badge>
            </>
          ) : (
            <>
              <ShieldOff className="h-4 w-4 text-amber-600" /> {t('security.mfa_title')}
              <Badge variant="warning">{t('common.disabled')}</Badge>
            </>
          )}
        </CardTitle>
        <CardDescription>
          {mfaEnabled ? t('security.mfa_enabled') : t('security.mfa_disabled')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!mfaEnabled && !enrolment && (
          <Button onClick={onEnrol} disabled={enrol.isPending}>
            {t('security.enrol')}
          </Button>
        )}

        {!mfaEnabled && enrolment && (
          <div className="space-y-4">
            <p className="text-sm">{t('security.scan_qr')}</p>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="rounded-md border border-border bg-white p-3">
                <QRCodeSVG value={enrolment.otpauth_uri} size={160} level="M" />
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('security.account_label')}
                  </div>
                  <div className="font-mono">{enrolment.account}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {t('security.secret_label')}
                  </div>
                  <code className="text-xs break-all">{enrolment.secret}</code>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="space-y-1.5 flex-1">
                <Label htmlFor="otp">{t('security.code')}</Label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  maxLength={8}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, ''))}
                />
              </div>
              <Button onClick={onConfirm} disabled={confirm.isPending || code.length < 6}>
                {t('security.confirm')}
              </Button>
              <Button variant="ghost" onClick={() => setEnrolment(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}

        {mfaEnabled && (
          <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
            <Button variant="destructive" onClick={() => setDisableOpen(true)}>
              {t('security.disable')}
            </Button>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('security.disable')}</DialogTitle>
                <DialogDescription>{t('security.password_to_disable')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label htmlFor="dpw">{t('auth.password')}</Label>
                <Input
                  id="dpw"
                  type="password"
                  autoComplete="current-password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDisableOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={onDisable}
                  disabled={disable.isPending || disablePassword.length === 0}
                >
                  {t('security.disable')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}
